import {AbstractService} from '@/services/core/serviceFactory';
import {patientsGraphqlService} from '@/services/api';
import {notificationsGraphqlService} from '@/services/api/notifications/notificationsGraphqlService';
import {clinicsGraphqlService} from '@/services/api/clinics/clinicsGraphqlService';

export interface SMSPatient {
    patient_clinic_data_id: number;
    first_name: string;
    last_name: string;
    mobile_phone_number: string;
    clinic_name: string;
}

export interface SMSTemplate {
    template_id: number;
    template_name: string;
    template: string;
    template_key: string;
    is_active: boolean;
}

export interface SMSTemplatesResponse {
    api_sms_template: SMSTemplate[];
}

export interface SMSPatientsResponse {
    pep_vw_consolidated_data: SMSPatient[];
}

export interface SendSMSResponse {
    success: boolean;
    message?: string;
    data?: any;
}

export interface SendEmailResponse {
    success: boolean;
    message?: string;
    data?: any;
}

export interface OutboundNumber {
    phone_number: string;
    number_source_id: number;
    origin_number_type_id: number;
    is_active: boolean;
    is_global: boolean;
}

export class CommunicationService extends AbstractService {
    /**
     * Fetch patient data for SMS by patient ID
     */
    async fetchSMSPatientData(patientId: number): Promise<SMSPatientsResponse> {
        this.requireAuth();

        if (!patientId) {
            throw new Error('Patient ID is required');
        }

        try {
            const result = await patientsGraphqlService.getSMSPatientData(
                this.context.hasuraToken!,
                patientId
            );
            return {pep_vw_consolidated_data: result.data?.pep_vw_consolidated_data || []} as unknown as SMSPatientsResponse;
        } catch (error) {
            throw new Error('Failed to fetch SMS patient data');
        }
    }

    /**
     * Fetch SMS templates
     */
    async fetchSMSTemplates(): Promise<{ success: boolean; data?: SMSTemplatesResponse; errors?: string[] }> {
        this.requireAuth();

        try {
            const result = await notificationsGraphqlService.getSMSTemplates(this.context.hasuraToken!);
            return {
                success: true,
                data: {api_sms_template: result || []}
            };
        } catch (error) {
            return {
                success: false,
                errors: ['Failed to fetch SMS templates']
            };
        }
    }

    /**
     * Send SMS using Twilio service
     */
    async sendSMS(
        from: string,
        to: string,
        patientId: number,
        templateId?: number,
        message?: string,
        clinicId?: number
    ): Promise<SendSMSResponse> {
        this.requireAuth();
        this.requireUserId();

        if (!from) {
            throw new Error('From number is required');
        }
        if (!to) {
            throw new Error('To number is required');
        }
        if (!patientId) {
            throw new Error('Patient ID is required');
        }

        try {
            const requestBody: any = {
                from: from.trim(),
                to: to.trim(),
                patientId,
                senderId: this.context.userId,
                clinicId,
                key: "PEP"
            };

            // Only include templateId if provided
            if (templateId) {
                requestBody.templateId = templateId;
            }

            // Only include message if provided and not empty
            if (message && message.trim()) {
                requestBody.message = message.trim();
            }

            const result = await this.apiClient.post(`/twilio/sms/send`, requestBody, this.context.token);

            return {
                success: result.data?.success || true,
                message: result.data?.message || 'SMS sent successfully',
                data: result.data
            };
        } catch (error) {
            throw error;
        }
    }

    /**
     * Send email with group details
     */
    async sendGroupDetailsEmail(groupId: number, emails: string[], password: string): Promise<SendEmailResponse> {
        this.requireAuth();
        this.requireUserId();

        if (!groupId) {
            throw new Error('Group ID is required');
        }

        if (!emails || emails.length === 0) {
            throw new Error('Email addresses are required');
        }

        if (!password) {
            throw new Error('Password is required');
        }

        try {
            const result = await this.apiClient.post(`/api/pep/${this.context.userId}/sendGroupDetails/${groupId}`, {
                emails: emails,
                password: password
            }, this.context.token);

            return {
                success: result.data?.success || true,
                message: result.data?.message || 'Email sent successfully',
                data: result.data
            };
        } catch (error) {
            console.error(error)
            throw error;
        }
    }

    /**
     * Format phone number for display
     */
    formatPhoneNumber(phoneNumber: string): string {
        if (!phoneNumber || phoneNumber === 'n/a') {
            return 'n/a';
        }

        // Remove all non-digits
        const digits = phoneNumber.replace(/\D/g, '');

        // Format as (XXX) XXX-XXXX if 10 digits
        if (digits.length === 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        }

        return phoneNumber;
    }

    /**
     * Validate phone number
     */
    isValidPhoneNumber(phoneNumber: string): boolean {
        if (!phoneNumber || phoneNumber === 'n/a') {
            return false;
        }

        // Remove all non-digits
        const digits = phoneNumber.replace(/\D/g, '');

        // Check if it's a valid 10-digit US phone number
        return digits.length === 10;
    }

    /**
     * Fetch outbound clinic numbers
     */
    async fetchOutboundNumbers(clinicId: number, stateId: number): Promise<{ success: boolean; data?: OutboundNumber[]; errors?: string[] }> {
        this.requireAuth();

        if (!clinicId || !stateId) {
            return {
                success: false,
                errors: ['Clinic ID and State ID are required']
            };
        }

        try {
            const result = await clinicsGraphqlService.getClinicNumbers(clinicId, stateId, this.context.hasuraToken!);
            
            // Transform the response to match our interface
            const outboundNumbers: OutboundNumber[] = result.map(item => ({
                phone_number: item.phone_number,
                number_source_id: item.number_source_id,
                origin_number_type_id: item.origin_number_type_id,
                is_active: item.is_active,
                is_global: item.is_global
            }));

            return {
                success: true,
                data: outboundNumbers
            };
        } catch (error) {
            console.error('Error fetching outbound numbers:', error);
            return {
                success: false,
                errors: ['Failed to fetch outbound numbers']
            };
        }
    }
}